// packages/frontend/src/hooks/useApiQueries.ts
import { useQuery } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import { format } from 'date-fns';

import { makeAuthenticatedApiCall } from '../apiHelper';
import { CGListData, CGStatistics } from '../types/cg';
import { DBDeal, FranchiseData, MarketplaceDeal } from '../types/deal';
import { PromoMessageSlot } from '../types/promoMessage';

interface MonthlyStats {
  currentMonth: {
    totalCotations: number;
    totalCotationsPotentialValue: number;
    totalCotationsSkipcarPotentialValue: number;
    totalBuying: number;
    totalBuyingValue: number;
    totalBuyingSkipcarValue: number;
    totalSelling: number;
    totalSellingValue: number;
    totalNetMarginValue: number;
  };
  previousMonth: {
    totalCotations: number;
    totalCotationsPotentialValue: number;
    totalCotationsSkipcarPotentialValue: number;
    totalBuying: number;
    totalBuyingValue: number;
    totalBuyingSkipcarValue: number;
    totalSelling: number;
    totalSellingValue: number;
    totalNetMarginValue: number;
  };
}

interface DailyBuyingStats {
  date: string;
  quotations: number;
  purchases: number;
  transformation_rate: number;
}

interface DailySellingStats {
  date: string;
  net_amount: number;
  net_margin: number;
}

interface AgencyPerformance {
  cotations: {
    org_name: string;
    count: number;
  }[];
  buying: {
    org_name: string;
    count: number;
  }[];
  selling: {
    org_name: string;
    count: number;
    net_amount: string;
    net_margin: number;
  }[];
}

export interface DAItem {
  invoice_url_firebase: any;
  invoice_url: any;
  // Pas de champ `id` ici : la liste DA n'expose que `RegistrationID`
  // (`daModel.getDAList` aliase `R.id as RegistrationID`). Un `id` déclaré ici
  // valait `undefined` à l'exécution et servait de clé React aux lignes du
  // tableau, ce qui faisait recycler les composants d'une ligne à l'autre.
  RegistrationID: number;
  regPlate: string;
  vin: string;
  numFormule: string;
  purchaseDateTime: string;
  createdAt: string;
  updatedAt: string;
  status: string;
  visible: boolean;
  AgencyID: number;
  AgencyName: string;
  BuyerFirstName: string;
  BuyerLastName: string;
  BuyerCompanyName: string;
  BuyerClientType: string;
  SellerFirstName: string;
  SellerLastName: string;
  SellerCompanyName: string;
  SellerClientType: string;
  invoice_number: string;
  invoice_status?: string;
  invoiceUrl: string;
  qontoStep: number;
  daFee: number | string;
  comments?: Array<{
    user: string;
    date: string;
    comment: string;
  }>;
  hasExternalComments?: boolean;
}

interface DAListData {
  data: DAItem[];
  total: number;
}

interface DAListResponse {
  data: DAListData;
  message?: string;
}

interface ExternalCommentDA {
  comment_id: number;
  registration_id: number;
  user_id: number;
  user_role: string;
  user_name: string;
  comment: string;
  created_at: string;
}

const fetchDealExistence = async (id: number): Promise<boolean> => {
  if (id < 8718) {
    // Premier ID en base, évite de tout tester
    return false;
  }

  try {
    const response = await axios.get(`/api/deals/exists/${id}`);
    return response.data.exists;
  } catch (error) {
    console.error('Error checking deal existence:', error);
    throw error;
  }
};

export const useDealExistence = (pipedriveDealId: number) => {
  return useQuery({
    queryKey: ['dealExistence', pipedriveDealId],
    queryFn: () => fetchDealExistence(pipedriveDealId),
  });
};

const fetchSalesPersons = async (orgId: string): Promise<any[]> => {
  try {
    const response = await axios.get(`/api/pipedrive/sales-persons?orgId=${orgId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching sales persons:', error);
    throw error;
  }
};

export const useSalesPersons = (orgId: string) => {
  return useQuery({
    queryKey: ['salesPersons', orgId],
    queryFn: () => fetchSalesPersons(orgId),
  });
};

const fetchOrganizationInfo = async (orgId: string) => {
  const response = await axios.get(`/api/pipedrive/organizations/${orgId}`);
  return response.data;
};

export const useOrganizationInfo = (orgId: string) => {
  return useQuery({
    queryKey: ['organizationInfo', orgId],
    queryFn: () => fetchOrganizationInfo(orgId),
  });
};

const fetchPersonInfo = async (personId: string) => {
  const response = await axios.get(`/api/pipedrive/persons/${personId}`);
  return response.data;
};

export const usePersonInfo = (orgId: string) => {
  return useQuery({
    queryKey: ['personInfo', orgId],
    queryFn: () => fetchPersonInfo(orgId),
  });
};
const fetchOrganizationInDb = async (orgId: string) => {
  const response = await axios.get(`/api/pipedrive/organizations-in-db/${orgId}`);
  return response.data;
};

export const useOrganizationInDb = (orgId: string) => {
  return useQuery({
    queryKey: ['organizationInDb', orgId],
    queryFn: () => fetchOrganizationInDb(orgId),
  });
};

const fetchPersonInDb = async (personId: string) => {
  const response = await axios.get(`/api/pipedrive/persons-in-db/${personId}`);
  return response.data;
};

export const usePersonInDb = (personId: string) => {
  return useQuery({
    queryKey: ['personInDb', personId],
    queryFn: () => fetchPersonInDb(personId),
  });
};

const fetchPersonByEmail = async (email: string) => {
  const response = await axios.get(`/api/pipedrive/persons-by-email/${encodeURIComponent(email)}`);
  return response.data;
};

export const usePersonByEmail = (email: string) => {
  return useQuery({
    queryKey: ['personByEmail', email],
    queryFn: () => fetchPersonByEmail(email),
    enabled: !!email,
  });
};

const fetchPersonWithOrgByEmail = async (email: string) => {
  const response = await axios.get(`/api/pipedrive/persons-with-org-by-email/${encodeURIComponent(email)}`);
  return response.data;
};

export const usePersonWithOrgByEmail = (email: string) => {
  return useQuery({
    queryKey: ['personWithOrgByEmail', email],
    queryFn: () => fetchPersonWithOrgByEmail(email),
    enabled: !!email,
  });
};

const fetchStages = async () => {
  try {
    const response = await axios.get('/api/pipedrive/stages');
    return response.data;
  } catch (error) {
    console.error('Error fetching stages:', error);
    // Retourner un tableau vide en cas d'erreur pour ne pas bloquer l'UI
    return [];
  }
};

export const useStages = () => {
  return useQuery({
    queryKey: ['stages'],
    queryFn: fetchStages,
    staleTime: 2 * 7 * 24 * 60 * 60 * 1000, // 2 weeks in milliseconds
    retry: 1, // Ne réessayer qu'une seule fois en cas d'échec
  });
};

const fetchPipelines = async () => {
  try {
    const response = await axios.get('/api/pipedrive/pipelines');
    return response.data;
  } catch (error) {
    console.error('Error fetching pipelines:', error);
    // Retourner un tableau vide en cas d'erreur pour ne pas bloquer l'UI
    return [];
  }
};

export const usePipelines = () => {
  return useQuery({
    queryKey: ['pipelines'],
    queryFn: fetchPipelines,
    staleTime: 2 * 7 * 24 * 60 * 60 * 1000, // 2 weeks in milliseconds
    retry: 1, // Ne réessayer qu'une seule fois en cas d'échec
  });
};

const fetchPipedriveDealInfo = async (dealId: string) => {
  const response = await axios.get(`/api/pipedrive/deals/${dealId}`);
  return response.data;
};

export const usePipedriveDealInfo = (dealId: string) => {
  return useQuery({
    queryKey: ['usePipedriveDealInfo', dealId],
    queryFn: () => fetchPipedriveDealInfo(dealId),
  });
};

const fetchDatabaseDealInfo = async (dealId: number, make: string, fuel: string) => {
  if (dealId < 8718) {
    // Premier ID en base, évite de tout tester
    return false;
  }
  const response = await axios.get(`/api/deals/${dealId}`, {
    params: {
      make,
      fuel,
    },
  });
  return response.data;
};

export const useDatabaseDealInfo = (dealId: number, make: string, fuel: string) => {
  return useQuery({
    queryKey: ['useDatabaseDealInfo', dealId],
    queryFn: () => fetchDatabaseDealInfo(dealId, make, fuel),
  });
};

const fetchDatabaseDealInfoAuth = async (dealId: number) => {
  if (dealId >= 8929) {
    const response = await makeAuthenticatedApiCall('get', `/api/deals/${dealId}/auth`);
    return response;
  }
  return '';
};

export const useDatabaseDealInfoAuth = (dealId: number) => {
  return useQuery({
    queryKey: ['useDatabaseDealInfoAuth', dealId],
    queryFn: () => fetchDatabaseDealInfoAuth(dealId),
  });
};

interface DatabaseDealsResponse {
  data: DBDeal[];
}

const fetchDatabaseDeals = async (orgId?: number | null, userRole?: string) => {
  const baseEndpoint = `/api/deals`;
  const params = new URLSearchParams();
  if (orgId) params.append('orgId', orgId.toString());
  if (userRole) params.append('userRole', userRole);

  const endpoint = `${baseEndpoint}${params.toString() ? `?${params.toString()}` : ''}`;
  const response = (await makeAuthenticatedApiCall('get', endpoint)) as AxiosResponse<DatabaseDealsResponse>;
  return response;
};

/**
 * Le backend refuse `GET /api/deals` sans `orgId` pour un compte agence : il lit
 * le rôle dans le jeton, pas dans l'URL (`carDealModel.getCarDeals`). Le garde
 * ci-dessous ne pouvait donc pas suffire côté front, puisqu'au premier rendu
 * `userRole` n'est pas encore chargé : `userRole !== 'agency'` était vrai, la
 * requête partait sans paramètre et revenait en 400, rejouée par react-query.
 * Quatre à cinq allers-retours inutiles par chargement de page et par
 * utilisateur, chacun consommant une vérification de jeton Firebase.
 *
 * Exporté pour être testé isolément : c'est la seule logique de la requête.
 */
export const shouldFetchDatabaseDeals = (orgId?: number | null, userRole?: string): boolean => {
  if (!userRole) return false; // rôle pas encore chargé : on ne devine pas
  return userRole !== 'agency' || (!!orgId && orgId > 0);
};

/**
 * `enabled: false` pour les appelants qui ne veulent QUE `refetch` et ne lisent
 * ni `data`, ni `isLoading`, ni `error` — c'est le cas de MarketplaceDealsList,
 * CaseArbitration et CreateCotation. `refetch()` reste opérant sur une requête
 * désactivée (react-query v5), seul le déclenchement automatique disparaît.
 */
export const useDatabaseDeals = (orgId?: number | null, userRole?: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['useDatabaseDeals', orgId, userRole],
    queryFn: () => fetchDatabaseDeals(orgId, userRole),
    enabled: options?.enabled ?? shouldFetchDatabaseDeals(orgId, userRole),
  });
};

const fetchMarketplaceDeals = async () => {
  try {
    const response = await makeAuthenticatedApiCall<readonly MarketplaceDeal[]>(
      'get',
      '/api/marketplace/public/vehicles?limit=500&page=0',
    );
    console.log('response', response);
    return response;
  } catch (error) {
    throw new Error('Erreur lors de la récupération des marketplace deals');
  }
};

export const useMarketplaceDeals = () => {
  return useQuery({
    queryKey: ['useMarketplaceDeals'],
    queryFn: fetchMarketplaceDeals,
  });
};

const fetchArbitratorDeals = async (status?: string, limit?: number, orderByDateField?: boolean) => {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (limit) params.append('limit', limit.toString());
  if (orderByDateField) params.append('orderByDateField', orderByDateField.toString());

  const url = `/api/deals/arbitrator?${params.toString()}`;

  const response = await makeAuthenticatedApiCall('get', url);
  return response;
};

export const useArbitratorDeals = (status?: string, limit?: number, orderByDateField?: boolean) => {
  return useQuery({
    queryKey: ['useArbitratorDeals', status, limit, orderByDateField], // Inclure status, limit, et orderByDateField dans la clé de requête
    queryFn: () => fetchArbitratorDeals(status, limit, orderByDateField), // Passer status, limit, et orderByDateField à la fonction fetch
    refetchOnWindowFocus: false,
  });
};

const fetchArbitratorOffersForAgency = async (orgId: number) => {
  try {
    const response = await makeAuthenticatedApiCall('get', `/api/deals/arbitrator/${orgId}`);
    return response;
  } catch (error) {
    // Gérer l'erreur selon les besoins
    throw new Error("Erreur lors de la récupération des offres d'arbitrage pour l'agence");
  }
};

export const useArbitratorOffersForAgency = (orgId: number) => {
  return useQuery({
    queryKey: ['useArbitratorOffersForAgency', orgId],
    queryFn: () => fetchArbitratorOffersForAgency(orgId),
    enabled: !!orgId,
  });
};

const fetchArbitrationDeal = async (dealId: number) => {
  const response = await makeAuthenticatedApiCall('get', `/api/arbitration/${dealId}`);
  return response;
};

export const useArbitrationDeal = (dealId: number, shouldFetch: boolean = true) => {
  return useQuery({
    queryKey: ['useArbitrationDeal', dealId],
    queryFn: () => fetchArbitrationDeal(dealId),
    enabled: shouldFetch, // Exécute la requête uniquement si shouldFetch est true
    refetchOnWindowFocus: false,
    staleTime: 2000,
  });
};

export const useDealMisivA = (dealId: number) => {
  return useQuery({
    queryKey: ['useDealMisivA', dealId],
    queryFn: () => fetchDealMisivA(dealId),
  });
};
const fetchDealMisivA = async (dealId: number) => {
  const response = await makeAuthenticatedApiCall('get', `/api/misiv/deals/a/${dealId}`);
  return response;
};

export const useDealMisivB = (dealId: number) => {
  return useQuery({
    queryKey: ['useDealMisivB', dealId],
    queryFn: () => fetchDealMisivB(dealId),
  });
};
const fetchDealMisivB = async (dealId: number) => {
  const response = await makeAuthenticatedApiCall('get', `/api/misiv/deals/b/${dealId}`);
  return response;
};

export const useDealMisivC = (dealId: number) => {
  return useQuery({
    queryKey: ['useDealMisivC', dealId],
    queryFn: () => fetchDealMisivC(dealId),
  });
};
const fetchDealMisivC = async (dealId: number) => {
  const response = await makeAuthenticatedApiCall('get', `/api/misiv/deals/c/${dealId}`);
  return response;
};

export const useDealMisivE = (dealId: number) => {
  return useQuery({
    queryKey: ['useDealMisivE', dealId],
    queryFn: () => fetchDealMisivE(dealId),
  });
};
const fetchDealMisivE = async (dealId: number) => {
  const response = await makeAuthenticatedApiCall('get', `/api/misiv/deals/e/${dealId}`);
  return response;
};
export const useDealMisivF = (dealId: number) => {
  return useQuery({
    queryKey: ['useDealMisivF', dealId],
    queryFn: () => fetchDealMisivF(dealId),
  });
};
const fetchDealMisivF = async (dealId: number) => {
  const response = await makeAuthenticatedApiCall('get', `/api/misiv/deals/f/${dealId}`);
  return response;
};

const fetchDepositSaleBuyer = async (dealId: number) => {
  const response = await makeAuthenticatedApiCall('get', `/api/misiv/deposit-sale-buyer/${dealId}`);
  return response.data;
};

export const useDepositSaleBuyer = (dealId: number) => {
  return useQuery({
    queryKey: ['useDepositSaleBuyer', dealId],
    queryFn: () => fetchDepositSaleBuyer(dealId),
  });
};

const fetchCGList = async (
  userPipedrive: number,
  userRole: string,
  page: number,
  limit: number,
  searchQuery: string,
  stageId?: number | null,
  sortColumn: string = 'R.RegistrationID',
  sortOrder: string = 'DESC',
  queryNonVisible: boolean = false,
  godCommentFilter?: string | null,
  cgStatusFilter?: string | null,
  docRequestOriginFilter?: string | null,
): Promise<CGListData> => {
  const stageIdParam = stageId ? `&stage_id=${stageId}` : '';
  const visibleParam = queryNonVisible ? '&visible=hidden' : '&visible=1';
  const godCommentParam = godCommentFilter ? `&god_comment_filter=${godCommentFilter}` : '';
  const cgStatusParam = cgStatusFilter ? `&cg_status=${cgStatusFilter}` : '';
  const docOriginParam = docRequestOriginFilter ? `&doc_request_origin=${docRequestOriginFilter}` : '';
  const response = await makeAuthenticatedApiCall<CGListData>(
    'get',
    `/api/cg/?org_id=${userPipedrive}&user_role=${userRole}&page=${page}&limit=${limit}&search=${searchQuery}${stageIdParam}&sortColumn=${sortColumn}&sortOrder=${sortOrder}${visibleParam}${godCommentParam}${cgStatusParam}${docOriginParam}`,
  );
  return response.data;
};

export const useCGList = (
  userPipedrive: number,
  userRole: string,
  page: number,
  limit: number,
  searchQuery: string,
  stageId?: number | null,
  sortColumn: string = 'R.RegistrationID',
  sortOrder: string = 'DESC',
  queryNonVisible: boolean = false,
  godCommentFilter?: string | null,
  cgStatusFilter?: string | null,
  docRequestOriginFilter?: string | null,
) => {
  return useQuery<CGListData>({
    queryKey: [
      'useCGList',
      userPipedrive,
      userRole,
      page,
      limit,
      searchQuery,
      stageId,
      sortColumn,
      sortOrder,
      queryNonVisible,
      godCommentFilter,
      cgStatusFilter,
      docRequestOriginFilter,
    ],
    queryFn: () =>
      fetchCGList(
        userPipedrive,
        userRole,
        page,
        limit,
        searchQuery,
        stageId,
        sortColumn,
        sortOrder,
        queryNonVisible,
        godCommentFilter,
        cgStatusFilter,
        docRequestOriginFilter,
      ),
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
};

/** Infos véhicule (sous-ensemble) renvoyées par /api/plaque/regplate. */
export interface VehicleInfoLite {
  Marque?: string | null;
  Modele?: string | null;
  Version?: string | null;
  reg_plate?: string | null;
}

/**
 * Résout marque/modèle depuis une plaque via l'API Plaque
 * (`GET /api/plaque/regplate`) : le controller vérifie d'abord `info_immat` en
 * base, et n'appelle l'API externe qu'en l'absence de cache (résultat persisté).
 * Données véhicule statiques → cache session (staleTime Infinity) et pas de
 * retry (plaque inconnue / API indispo). Utilisé pour afficher le vrai véhicule
 * du dossier CG par sa plaque, au lieu d'un libellé issu d'un autre pipeline.
 */
export const useVehicleInfo = (regPlate: string | null | undefined) => {
  const plate = (regPlate ?? '').trim();
  return useQuery<VehicleInfoLite | null>({
    queryKey: ['useVehicleInfo', plate],
    queryFn: async () => {
      const response = await makeAuthenticatedApiCall<VehicleInfoLite>(
        'get',
        `/api/plaque/regplate?regPlate=${encodeURIComponent(plate)}`,
      );
      return response.data ?? null;
    },
    enabled: plate.length > 0,
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useCGData = (registrationId: string) => {
  if (!registrationId) {
    return { data: null, isLoading: false };
  }
  return useQuery({
    queryKey: ['cgData', registrationId],
    queryFn: () => fetchCGData(registrationId),
    enabled: !!registrationId,
  });
};

export const useCGDocumentsStructured = (registrationId: string) => {
  return useQuery({
    queryKey: ['cgDocumentsStructured', registrationId],
    queryFn: () => makeAuthenticatedApiCall('get', `/api/cg/${registrationId}/documents`),
    enabled: !!registrationId,
  });
};

const fetchCGData = async (registrationId: string) => {
  const response = await makeAuthenticatedApiCall('get', `/api/cg/${registrationId}`);
  return response.data;
};

export const useDealsByIds = (ids: number[]) => {
  return useQuery({
    queryKey: ['useDealsByIds'],
    queryFn: () => fetchDealsByIds(ids),
  });
};

const fetchDealsByIds = async (ids: number[]) => {
  const response = await makeAuthenticatedApiCall('post', '/api/deals-by-ids', { ids });
  return response.data;
};

/*
export const useDealQonto = (dealId: number, pipelineLetter: string) => {
  return useQuery({
    queryKey: ['useDealQonto', dealId, pipelineLetter],
    queryFn: () => fetchDealQonto(dealId, pipelineLetter),
    staleTime: 0,
  });
};

const fetchDealQonto = async (dealId: number, pipelineLetter: string) => {
  const response = await makeAuthenticatedApiCall('get', `/api/qonto/deals/${pipelineLetter}/${dealId}`);
  return response?.data;
};

*/

export const useCGStatistics = () => {
  return useQuery({
    queryKey: ['useCGStatistics'],
    queryFn: () => fetchCGStatistics(),
  });
};

const fetchCGStatistics = async () => {
  const response = await makeAuthenticatedApiCall<CGStatistics>('get', `/api/cg/statistics`);
  return response.data;
};

export const useGetFranchise = () => {
  return useQuery({
    queryKey: ['useGetFranchise'],
    queryFn: () => fetchGetFranchise(),
    staleTime: 2 * 7 * 24 * 60 * 60 * 1000, // 2 weeks in milliseconds
  });
};

const fetchGetFranchise = async () => {
  const response = await makeAuthenticatedApiCall<FranchiseData>('get', `/api/statistics/franchise`);
  return response.data;
};

export const useGetAgencies = () => {
  return useQuery({
    queryKey: ['useGetAgencies'],
    queryFn: () => fetchGetAgencies(),
    staleTime: 2 * 7 * 24 * 60 * 60 * 1000, // 2 weeks in milliseconds
  });
};

const fetchGetAgencies = async () => {
  const response = await makeAuthenticatedApiCall('get', `/api/statistics/agencies`);
  return response.data;
};

// Hook pour obtenir les statistiques de cotations par date
const fetchStatisticsCountByDate = async (startDate: string, endDate: string, franchise: string, agency: string) => {
  const response = await makeAuthenticatedApiCall(
    'get',
    `/api/statistics/count-by-date?startDate=${startDate}&endDate=${endDate}&franchise=${encodeURIComponent(franchise)}&org_name=${encodeURIComponent(agency)}`,
  );
  return response.data;
};

export const useStatisticsCountByDate = (startDate: Date, endDate: Date, franchise: string, agency: string) => {
  const formattedStartDate = format(startDate, 'yyyy-MM-dd');
  const formattedEndDate = format(endDate, 'yyyy-MM-dd');
  return useQuery({
    queryKey: ['statisticsCountByDate', formattedStartDate, formattedEndDate, franchise, agency],
    queryFn: () => fetchStatisticsCountByDate(formattedStartDate, formattedEndDate, franchise, agency),
    staleTime: 24 * 60 * 60 * 1000, // 24 heures
  });
};

// Hook pour les statistiques d'achats
const fetchBuyingStatistics = async (startDate: string, endDate: string, franchise: string, agency: string) => {
  const response = await makeAuthenticatedApiCall(
    'get',
    `/api/statistics/buying?startDate=${startDate}&endDate=${endDate}&franchise=${encodeURIComponent(franchise)}&org_name=${encodeURIComponent(agency)}`,
  );
  return response.data;
};

export const useBuyingStatistics = (startDate: Date, endDate: Date, franchise: string, agency: string) => {
  const formattedStartDate = format(startDate, 'yyyy-MM-dd');
  const formattedEndDate = format(endDate, 'yyyy-MM-dd');
  return useQuery({
    queryKey: ['buyingStatistics', formattedStartDate, formattedEndDate, franchise, agency],
    queryFn: () => fetchBuyingStatistics(formattedStartDate, formattedEndDate, franchise, agency),
    staleTime: 24 * 60 * 60 * 1000, // 24 heures
  });
};

// Hook pour les statistiques de ventes
const fetchSellingStatistics = async (startDate: string, endDate: string, franchise: string, agency: string) => {
  const response = await makeAuthenticatedApiCall(
    'get',
    `/api/statistics/selling?startDate=${startDate}&endDate=${endDate}&franchise=${encodeURIComponent(franchise)}&org_name=${encodeURIComponent(agency)}`,
  );
  return response.data;
};

export const useSellingStatistics = (startDate: Date, endDate: Date, franchise: string, agency: string) => {
  const formattedStartDate = format(startDate, 'yyyy-MM-dd');
  const formattedEndDate = format(endDate, 'yyyy-MM-dd');
  return useQuery({
    queryKey: ['sellingStatistics', formattedStartDate, formattedEndDate, franchise, agency],
    queryFn: () => fetchSellingStatistics(formattedStartDate, formattedEndDate, franchise, agency),
    staleTime: 24 * 60 * 60 * 1000, // 24 heures
  });
};

// Hook pour les statistiques d'arbitrages
const fetchArbitrationStatistics = async (
  type: string,
  startDate: string,
  endDate: string,
  franchise: string,
  agency: string,
) => {
  const response = await makeAuthenticatedApiCall(
    'get',
    `/api/statistics/arbitrations/${type}?startDate=${startDate}&endDate=${endDate}&franchise=${encodeURIComponent(franchise)}&org_name=${encodeURIComponent(agency)}`,
  );
  return response.data;
};

export const useArbitrationStatistics = (
  type: string,
  startDate: Date,
  endDate: Date,
  franchise: string,
  agency: string,
) => {
  const formattedStartDate = format(startDate, 'yyyy-MM-dd');
  const formattedEndDate = format(endDate, 'yyyy-MM-dd');
  return useQuery({
    queryKey: ['arbitrationStatistics', type, formattedStartDate, formattedEndDate, franchise, agency],
    queryFn: () => fetchArbitrationStatistics(type, formattedStartDate, formattedEndDate, franchise, agency),
    staleTime: 24 * 60 * 60 * 1000, // 24 heures
  });
};

// Hook pour récupérer les agences top et flop
const fetchAgencyPerformance = async (
  performanceType: 'top' | 'flop',
  startDate: string,
  endDate: string,
  franchise: string,
) => {
  const response = await makeAuthenticatedApiCall(
    'get',
    `/api/statistics/agencies/${performanceType}?startDate=${startDate}&endDate=${endDate}&franchise=${encodeURIComponent(franchise)}`,
  );
  return response.data;
};

export const useAgencyPerformance = (
  performanceType: 'top' | 'flop',
  startDate: Date,
  endDate: Date,
  franchise: string,
) => {
  const formattedStartDate = format(startDate, 'yyyy-MM-dd');
  const formattedEndDate = format(endDate, 'yyyy-MM-dd');
  return useQuery({
    queryKey: ['agencyPerformance', performanceType, formattedStartDate, formattedEndDate, franchise],
    queryFn: () => fetchAgencyPerformance(performanceType, formattedStartDate, formattedEndDate, franchise),
    staleTime: 24 * 60 * 60 * 1000, // 24 heures
  });
};

const fetchArbitrationPerformance = async (
  performanceType: 'top' | 'flop',
  startDate: string,
  endDate: string,
  franchise: string,
) => {
  const response = await makeAuthenticatedApiCall(
    'get',
    `/api/statistics/arbitrations/${performanceType}?startDate=${startDate}&endDate=${endDate}&franchise=${encodeURIComponent(franchise)}`,
  );
  return response.data;
};

export const useArbitrationPerformance = (
  performanceType: 'top' | 'flop',
  startDate: Date,
  endDate: Date,
  franchise: string,
) => {
  const formattedStartDate = format(startDate, 'yyyy-MM-dd');
  const formattedEndDate = format(endDate, 'yyyy-MM-dd');
  return useQuery({
    queryKey: ['arbitrationPerformance', performanceType, formattedStartDate, formattedEndDate, franchise],
    queryFn: () => fetchArbitrationPerformance(performanceType, formattedStartDate, formattedEndDate, franchise),
    staleTime: 24 * 60 * 60 * 1000, // 24 heures
  });
};

const fetchStockOffers = async (franchise?: string, orgName?: string) => {
  const url =
    `/api/statistics/stock/offers?` +
    (franchise ? `&franchise=${encodeURIComponent(franchise)}` : '') +
    (orgName ? `&orgName=${encodeURIComponent(orgName)}` : '');
  const response = await makeAuthenticatedApiCall('get', url);
  return response.data;
};

export const useStockOffers = (franchise?: string, orgName?: string) => {
  return useQuery({
    queryKey: ['stockOffers', franchise, orgName],
    queryFn: () => fetchStockOffers(franchise, orgName),
    staleTime: 24 * 60 * 60 * 1000, // 1 day in milliseconds
  });
};

const fetchStock = async (franchise?: string, orgName?: string) => {
  const params = new URLSearchParams();
  if (franchise) params.append('franchise', franchise);
  if (orgName) params.append('orgName', orgName);
  const url = `/api/statistics/stock?${params.toString()}`;
  const response = await makeAuthenticatedApiCall('get', url);
  return response.data;
};

export const useStock = (franchise?: string, orgName?: string) => {
  return useQuery({
    queryKey: ['stock', franchise, orgName],
    queryFn: () => fetchStock(franchise, orgName),
    staleTime: 24 * 60 * 60 * 1000, // 1 day in milliseconds
  });
};

export const fetchTopAgencies = async (startDate: string, endDate: string, franchise: string) => {
  const response = await makeAuthenticatedApiCall<AgencyPerformance[]>(
    'get',
    `/api/statistics/agencies/top?startDate=${startDate}&endDate=${endDate}&franchise=${encodeURIComponent(franchise)}`,
  );
  return response.data;
};

export const useTopAgencies = (startDate: Date, endDate: Date, franchise: string) => {
  const formattedStartDate = format(startDate, 'yyyy-MM-dd');
  const formattedEndDate = format(endDate, 'yyyy-MM-dd');

  return useQuery({
    queryKey: ['topAgencies', formattedStartDate, formattedEndDate, franchise],
    queryFn: () => fetchTopAgencies(formattedStartDate, formattedEndDate, franchise),
    staleTime: 24 * 60 * 60 * 1000, // 1 day in milliseconds
  });
};

export const fetchFlopTransformations = async (startDate: string, endDate: string, franchise: string) => {
  try {
    const response = await makeAuthenticatedApiCall(
      'get',
      `/api/statistics/transformations/flop?startDate=${startDate}&endDate=${endDate}&franchise=${encodeURIComponent(franchise)}`,
    );
    // S'assurer que nous retournons toujours un tableau
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching flop agencies:', error);
    return []; // Retourner un tableau vide en cas d'erreur
  }
};

export const useFlopTransformations = (startDate: Date, endDate: Date, franchise: string) => {
  const formattedStartDate = format(startDate, 'yyyy-MM-dd');
  const formattedEndDate = format(endDate, 'yyyy-MM-dd');

  return useQuery({
    queryKey: ['flopTransformations', formattedStartDate, formattedEndDate, franchise],
    queryFn: () => fetchFlopTransformations(formattedStartDate, formattedEndDate, franchise),
    staleTime: 24 * 60 * 60 * 1000,
    // Ajouter un fallback pour s'assurer qu'on a toujours un tableau
    select: (data) => (Array.isArray(data) ? data : []),
  });
};

export const fetchTopTransformations = async (startDate: string, endDate: string, franchise: string) => {
  try {
    const response = await makeAuthenticatedApiCall(
      'get',
      `/api/statistics/transformations/top?startDate=${startDate}&endDate=${endDate}&franchise=${encodeURIComponent(franchise)}`,
    );
    // S'assurer que nous retournons toujours un tableau
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching top agencies:', error);
    return []; // Retourner un tableau vide en cas d'erreur
  }
};

export const useTopTransformations = (startDate: Date, endDate: Date, franchise: string) => {
  const formattedStartDate = format(startDate, 'yyyy-MM-dd');
  const formattedEndDate = format(endDate, 'yyyy-MM-dd');

  return useQuery({
    queryKey: ['topTransformations', formattedStartDate, formattedEndDate, franchise],
    queryFn: () => fetchTopTransformations(formattedStartDate, formattedEndDate, franchise),
    staleTime: 24 * 60 * 60 * 1000,
    // Ajouter un fallback pour s'assurer qu'on a toujours un tableau
    select: (data) => (Array.isArray(data) ? data : []),
  });
};

interface AgencyStats {
  activeAgencies: { count: number; agencies: string[] };
  reactivatedAgencies: { count: number; agencies: string[] };
  newActivations: { count: number; agencies: string[] };
  inactiveAgencies: { count: number; agencies: string[] };
  nonReactivatedAgencies: { count: number; agencies: string[] };
}

export const fetchAgencyStats = async (): Promise<AgencyStats> => {
  try {
    const response = await makeAuthenticatedApiCall<AgencyStats>('get', `/api/statistics/agencies/stats`);
    return response.data;
  } catch (error) {
    console.error('Error fetching agency stats:', error);
    return {
      activeAgencies: { count: 0, agencies: [] },
      reactivatedAgencies: { count: 0, agencies: [] },
      newActivations: { count: 0, agencies: [] },
      inactiveAgencies: { count: 0, agencies: [] },
      nonReactivatedAgencies: { count: 0, agencies: [] },
    };
  }
};

export const useAgencyStats = () => {
  return useQuery<AgencyStats>({
    queryKey: ['agencyStats'],
    queryFn: () => fetchAgencyStats(),
    staleTime: 24 * 60 * 60 * 1000, // 24 heures de cache
    select: (data) => ({
      activeAgencies: data?.activeAgencies ?? 0,
      reactivatedAgencies: data?.reactivatedAgencies ?? 0,
      newActivations: data?.newActivations ?? 0,
      inactiveAgencies: data?.inactiveAgencies ?? 0,
      nonReactivatedAgencies: data?.nonReactivatedAgencies ?? 0,
    }),
    retry: 1, // Ne réessaie qu'une fois en cas d'échec
    refetchOnWindowFocus: false, // Ne recharge pas automatiquement quand la fenêtre reprend le focus
  });
};

export const fetchMonthlyStats = async (franchise?: string, orgName?: string): Promise<MonthlyStats> => {
  try {
    const params = new URLSearchParams();
    if (franchise) params.append('franchise', franchise);
    if (orgName) params.append('org_name', orgName);

    const response = await makeAuthenticatedApiCall<MonthlyStats>(
      'get',
      `/api/statistics/monthly-stats?${params.toString()}`,
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching monthly stats:', error);
    return {
      currentMonth: {
        totalCotations: 0,
        totalCotationsPotentialValue: 0,
        totalCotationsSkipcarPotentialValue: 0,
        totalBuying: 0,
        totalBuyingValue: 0,
        totalBuyingSkipcarValue: 0,
        totalSelling: 0,
        totalSellingValue: 0,
        totalNetMarginValue: 0,
      },
      previousMonth: {
        totalCotations: 0,
        totalCotationsPotentialValue: 0,
        totalCotationsSkipcarPotentialValue: 0,
        totalBuying: 0,
        totalBuyingValue: 0,
        totalBuyingSkipcarValue: 0,
        totalSelling: 0,
        totalSellingValue: 0,
        totalNetMarginValue: 0,
      },
    };
  }
};

export const useMonthlyStats = (franchise?: string, orgName?: string) => {
  return useQuery<MonthlyStats>({
    queryKey: ['monthlyStats', franchise, orgName],
    queryFn: () => fetchMonthlyStats(franchise, orgName),
    staleTime: 24 * 60 * 60 * 1000,
  });
};

export const fetchDailyBuyingStats = async (franchise?: string, orgName?: string): Promise<DailyBuyingStats[]> => {
  try {
    const params = new URLSearchParams();
    if (franchise) params.append('franchise', franchise);
    if (orgName) params.append('org_name', orgName);

    const response = await makeAuthenticatedApiCall<DailyBuyingStats[]>(
      'get',
      `/api/statistics/daily-buying-stats?${params.toString()}`,
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching daily buying stats:', error);
    return [];
  }
};

export const useDailyBuyingStats = (franchise?: string, orgName?: string) => {
  return useQuery<DailyBuyingStats[]>({
    queryKey: ['dailyBuyingStats', franchise, orgName],
    queryFn: () => fetchDailyBuyingStats(franchise, orgName),
    staleTime: 24 * 60 * 60 * 1000,
  });
};

export const fetchDailySellingStats = async (franchise?: string, orgName?: string): Promise<DailySellingStats[]> => {
  try {
    const params = new URLSearchParams();
    if (franchise) params.append('franchise', franchise);
    if (orgName) params.append('org_name', orgName);

    const response = await makeAuthenticatedApiCall<DailySellingStats[]>(
      'get',
      `/api/statistics/daily-selling-stats?${params.toString()}`,
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching daily selling stats:', error);
    return [];
  }
};

export const useDailySellingStats = (franchise?: string, orgName?: string) => {
  return useQuery<DailySellingStats[]>({
    queryKey: ['dailySellingStats', franchise, orgName],
    queryFn: () => fetchDailySellingStats(franchise, orgName),
    staleTime: 24 * 60 * 60 * 1000,
  });
};

export const fetchDetailedStatistics = async (
  startDate: string,
  endDate: string,
  franchise: string,
  orgName: string,
) => {
  const response = await makeAuthenticatedApiCall(
    'get',
    `/api/statistics/detailed-statistics?startDate=${startDate}&endDate=${endDate}&franchise=${encodeURIComponent(franchise)}&org_name=${encodeURIComponent(orgName)}`,
  );
  return response.data;
};

export const useDetailedStatistics = (startDate: Date, endDate: Date, franchise: string, orgName: string) => {
  // Vérifier si les dates sont valides
  const formattedStartDate =
    startDate instanceof Date && !isNaN(startDate.getTime())
      ? format(startDate, 'yyyy-MM-dd')
      : format(new Date(), 'yyyy-MM-dd');

  const formattedEndDate =
    endDate instanceof Date && !isNaN(endDate.getTime())
      ? format(endDate, 'yyyy-MM-dd')
      : format(new Date(), 'yyyy-MM-dd');

  return useQuery({
    queryKey: ['detailedStatistics', formattedStartDate, formattedEndDate, franchise, orgName],
    queryFn: () => fetchDetailedStatistics(formattedStartDate, formattedEndDate, franchise, orgName),
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
  });
};

const fetchOrganizations = async () => {
  try {
    const response = await axios.get('/api/deals/organizations');
    return response.data;
  } catch (error) {
    console.error('fetchOrganizations - error', error);
    throw error;
  }
};

export const useOrganizations = () => {
  return useQuery({ queryKey: ['organizations'], queryFn: fetchOrganizations, staleTime: 24 * 60 * 60 * 1000 }); // 24 hours
};

export const fetchInvoicingType = async (clientId: string) => {
  const response = await makeAuthenticatedApiCall('get', `/api/qonto/invoicing-type/${clientId}`);
  return response.data;
};

export const useInvoicingType = (clientId: string) => {
  return useQuery({
    queryKey: ['invoicingType', clientId],
    queryFn: () => fetchInvoicingType(clientId),
  });
};

export const fetchInvoicingTypeByOrgId = async (orgId: number) => {
  const response = await makeAuthenticatedApiCall('get', `/api/qonto/invoicing-type-by-org-id/${orgId}`);
  console.log('fetchInvoicingTypeByOrgId / response.data / ', response.data);
  return response.data;
};

export const useInvoicingTypeByOrgId = (orgId: number) => {
  return useQuery({
    queryKey: ['invoicingTypeByOrgId', orgId],
    queryFn: () => fetchInvoicingTypeByOrgId(orgId),
  });
};

// Add new hook for mandate calculator
const fetchMandateCalculator = async (dealId: number) => {
  const response = await makeAuthenticatedApiCall('get', `/api/arbitration/mandate-calculator/${dealId}`);
  return response.data;
};

export const useMandateCalculator = (dealId: number) => {
  return useQuery({
    queryKey: ['mandateCalculator', dealId],
    queryFn: () => fetchMandateCalculator(dealId),
    staleTime: 0, // Always consider stale to allow easy refetching
  });
};

// Add new hook for deposit sale calculator
const fetchDepositSaleCalculator = async (dealId: number) => {
  const response = await makeAuthenticatedApiCall('get', `/api/arbitration/deposit-sale-calculator/${dealId}`);
  return response.data;
};

export const useDepositSaleCalculator = (dealId: number) => {
  return useQuery({
    queryKey: ['depositSaleCalculator', dealId],
    queryFn: () => fetchDepositSaleCalculator(dealId),
    staleTime: 0, // Always consider stale to allow easy refetching
  });
};

const fetchDAList = async (
  userPipedrive: number,
  userRole: string,
  page: number,
  limit: number,
  searchQuery: string,
  status?: string,
  sortColumn: string = 'R.id',
  sortOrder: string = 'DESC',
  queryNonVisible: boolean = false,
  orgId?: number,
): Promise<DAListData> => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    ...(searchQuery && { search: searchQuery }),
    ...(status && { status }),
    sortColumn,
    sortOrder,
    queryNonVisible: queryNonVisible.toString(),
    ...(orgId && { orgId: orgId.toString() }),
  });

  const response = await makeAuthenticatedApiCall<DAListData>('get', `/api/da/list?${params}`);
  return response.data;
};

export const useDAList = (
  userPipedrive: number,
  userRole: string,
  page: number,
  limit: number,
  searchQuery: string,
  status?: string,
  sortColumn: string = 'R.id',
  sortOrder: string = 'DESC',
  queryNonVisible: boolean = false,
  orgId?: number,
) => {
  return useQuery({
    queryKey: [
      'useDAList',
      userPipedrive,
      userRole,
      page,
      limit,
      searchQuery,
      status,
      sortColumn,
      sortOrder,
      queryNonVisible,
      orgId,
    ],
    queryFn: () =>
      fetchDAList(
        userPipedrive,
        userRole,
        page,
        limit,
        searchQuery,
        status,
        sortColumn,
        sortOrder,
        queryNonVisible,
        orgId,
      ),
    enabled: userRole !== 'agency' || (userRole === 'agency' && !!orgId),
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
};

const fetchDAData = async (registrationId: string) => {
  const [registrationResponse, buyerResponse, sellerResponse] = await Promise.all([
    makeAuthenticatedApiCall('get', `/api/da/registrations/${registrationId}`),
    makeAuthenticatedApiCall('get', `/api/da/buyers/${registrationId}`),
    makeAuthenticatedApiCall('get', `/api/da/sellers/${registrationId}`),
  ]);

  return {
    ...(registrationResponse.data || {}),
    buyer: buyerResponse.data,
    seller: sellerResponse.data,
  };
};

export const useDAData = (registrationId: string) => {
  return useQuery({
    queryKey: ['daData', registrationId],
    queryFn: () => fetchDAData(registrationId),
    enabled: !!registrationId,
  });
};

const fetchExternalCommentsDA = async (registrationId: number): Promise<ExternalCommentDA[]> => {
  const response = await makeAuthenticatedApiCall<ExternalCommentDA[]>(
    'get',
    `/api/da/external-comments/${registrationId}`,
  );
  return response.data;
};

export const useExternalCommentsDA = (registrationId: number) => {
  return useQuery({
    queryKey: ['externalCommentsDA', registrationId],
    queryFn: () => fetchExternalCommentsDA(registrationId),
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
};

// Cofinancement hooks
const fetchCofinancementList = async (params: {
  page: number;
  rowsPerPage: number;
  searchQuery: string;
  status?: string;
  sortColumn: string;
  sortOrder: string;
  queryNonVisible?: boolean;
}) => {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    rowsPerPage: params.rowsPerPage.toString(),
    searchQuery: params.searchQuery,
    sortColumn: params.sortColumn,
    sortOrder: params.sortOrder,
    queryNonVisible: params.queryNonVisible ? 'true' : 'false',
  });

  if (params.status) {
    queryParams.append('status', params.status);
  }

  const response = await makeAuthenticatedApiCall('get', `/api/cofinancement/list?${queryParams.toString()}`);
  return response.data;
};

export const useCofinancementList = (params: {
  page: number;
  rowsPerPage: number;
  searchQuery: string;
  status?: string;
  sortColumn: string;
  sortOrder: string;
  queryNonVisible?: boolean;
}) => {
  return useQuery({
    queryKey: [
      'cofinancementList',
      params.page,
      params.rowsPerPage,
      params.searchQuery,
      params.status,
      params.sortColumn,
      params.sortOrder,
      params.queryNonVisible,
    ],
    queryFn: () => fetchCofinancementList(params),
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
};

const fetchCofinancementCounts = async () => {
  const response = await makeAuthenticatedApiCall('get', '/api/cofinancement/counts');
  return response.data;
};

export const useCofinancementCounts = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ['cofinancementCounts'],
    queryFn: fetchCofinancementCounts,
    enabled,
    staleTime: 0, // Pas de cache, données toujours considérées comme périmées
    refetchOnWindowFocus: true, // Recharger quand la fenêtre reprend le focus
    refetchInterval: false, // Pas de polling automatique, mais peut être activé si nécessaire
  });
};

const fetchCofinancementData = async (dealId: string) => {
  const response = await makeAuthenticatedApiCall('get', `/api/cofinancement/${dealId}`);
  return response.data;
};

export const useCofinancementData = (dealId: string) => {
  return useQuery({
    queryKey: ['cofinancementData', dealId],
    queryFn: () => fetchCofinancementData(dealId),
    enabled: !!dealId,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
};

const generateCofinancementSummaryPdf = async (dealId: string) => {
  const response = await makeAuthenticatedApiCall('get', `/api/cofinancement/${dealId}/summary-pdf`);
  return response.data;
};

export const useGenerateCofinancementSummaryPdf = (dealId: string) => {
  return useQuery({
    queryKey: ['cofinancementSummaryPdf', dealId],
    queryFn: () => generateCofinancementSummaryPdf(dealId),
    enabled: false, // Ne pas exécuter automatiquement
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
};

// ===== Promotional Banner =====

interface PromotionalBannerConfig {
  id?: number;
  is_active: boolean;
  title: string;
  subtitle?: string;
  content?: string;
  footer_text?: string;
  border_color: string;
  background_color: string;
  text_color: string;
  excluded_franchises?: string[];
  created_at?: Date;
  updated_at?: Date;
}

const fetchPromotionalBannerConfig = async () => {
  const response = await makeAuthenticatedApiCall('get', '/api/promotional-banner/config');
  return response.data;
};

export const usePromotionalBannerConfig = () => {
  return useQuery({
    queryKey: ['promotionalBannerConfig'],
    queryFn: () => fetchPromotionalBannerConfig(),
    staleTime: 5 * 60 * 1000, // Cache 5 minutes
    refetchOnWindowFocus: true,
    placeholderData: (previousData) => previousData, // Garder les données précédentes pendant un refetch
  });
};

const fetchAdminPromotionalBannerConfig = async () => {
  const response = await makeAuthenticatedApiCall('get', '/api/promotional-banner/admin/config');
  return response.data;
};

export const useAdminPromotionalBannerConfig = () => {
  return useQuery({
    queryKey: ['adminPromotionalBannerConfig'],
    queryFn: () => fetchAdminPromotionalBannerConfig(),
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
};

// Agency Home Promo Message hooks
// Le message promo est configuré par emplacement d'affichage (slot) : la page
// de garde et les affaires en cours ont chacune leur propre message, donc leur
// propre entrée de cache.
const fetchAgencyHomePromoMessageConfig = async (slot: PromoMessageSlot) => {
  const response = await makeAuthenticatedApiCall(
    'get',
    `/api/agency-home-promo-message/config?slot=${encodeURIComponent(slot)}`,
  );
  return response.data;
};

export const useAgencyHomePromoMessageConfig = (slot: PromoMessageSlot = 'home') => {
  return useQuery({
    queryKey: ['agencyHomePromoMessageConfig', slot],
    queryFn: () => fetchAgencyHomePromoMessageConfig(slot),
    staleTime: 5 * 60 * 1000, // Cache 5 minutes
    refetchOnWindowFocus: true,
  });
};

const fetchAdminAgencyHomePromoMessageConfig = async (slot: PromoMessageSlot) => {
  const response = await makeAuthenticatedApiCall(
    'get',
    `/api/agency-home-promo-message/admin/config?slot=${encodeURIComponent(slot)}`,
  );
  return response.data;
};

export const useAdminAgencyHomePromoMessageConfig = (slot: PromoMessageSlot = 'home') => {
  return useQuery({
    queryKey: ['adminAgencyHomePromoMessageConfig', slot],
    queryFn: () => fetchAdminAgencyHomePromoMessageConfig(slot),
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
};

// RFA hooks
const fetchRFAFees = async () => {
  const response = await makeAuthenticatedApiCall('get', '/api/rfa/fees');
  return response.data;
};

export const useRFAFees = () => {
  return useQuery({
    queryKey: ['rfaFees'],
    queryFn: () => fetchRFAFees(),
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
};

const fetchRFAMappings = async () => {
  const response = await makeAuthenticatedApiCall('get', '/api/rfa/mappings');
  return response.data;
};

export const useRFAMappings = () => {
  return useQuery({
    queryKey: ['rfaMappings'],
    queryFn: () => fetchRFAMappings(),
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
};

// Organizations and Persons hooks
const fetchOrganizationsList = async () => {
  const response = await makeAuthenticatedApiCall('get', '/api/pipedrive/organizations');
  return response.data;
};

export const useOrganizationsList = () => {
  return useQuery({
    queryKey: ['organizationsList'],
    queryFn: () => fetchOrganizationsList(),
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
};

const fetchPersonsList = async () => {
  const response = await makeAuthenticatedApiCall('get', '/api/pipedrive/persons');
  return response.data;
};

export const usePersonsList = () => {
  return useQuery({
    queryKey: ['personsList'],
    queryFn: () => fetchPersonsList(),
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
};

// Interface pour la réponse des frais client
interface ClientFeesResponse {
  success: boolean;
  data: {
    cgFee: number;
    daFee: number;
  };
}

// Hook pour récupérer les frais dynamiques pour un client spécifique
const fetchClientFees = async (clientId: string): Promise<ClientFeesResponse> => {
  const response = await makeAuthenticatedApiCall<ClientFeesResponse>('get', `/api/rfa/client-fees/${clientId}`);
  return response.data;
};

export const useClientFees = (clientId: string | null | undefined) => {
  return useQuery<ClientFeesResponse>({
    queryKey: ['clientFees', clientId],
    queryFn: () => fetchClientFees(clientId!),
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000, // Cache 5 minutes
  });
};

// ─────────────────────────────────────────────────────────────
// Agency fees : résolution directe agencyID (Pipedrive) → franchise → cgFee
// Utilisé par FormCGIA v2 avant la création de la CG (pas encore de Qonto
// client_id). Retourne juste `cgFee`, avec fallback 10€ côté backend.
// ─────────────────────────────────────────────────────────────
interface AgencyFeesResponse {
  success: boolean;
  data: { cgFee: number };
}

const fetchAgencyFees = async (agencyId: string | number): Promise<AgencyFeesResponse> => {
  const response = await makeAuthenticatedApiCall<AgencyFeesResponse>('get', `/api/rfa/agency-fees/${agencyId}`);
  return response.data;
};

export const useAgencyFees = (agencyId: string | number | null | undefined) => {
  return useQuery<AgencyFeesResponse>({
    queryKey: ['agencyFees', agencyId],
    queryFn: () => fetchAgencyFees(agencyId!),
    enabled: agencyId !== null && agencyId !== undefined && agencyId !== '',
    staleTime: 5 * 60 * 1000,
  });
};
